/**
 * Picking up a new deploy without a fight (Charles, 2026-09-20: a change took
 * "4 swipe ups of the web app" to appear on iPhone).
 *
 * Why it took four: iOS RESUMES an installed web app instead of reloading it,
 * so the browser's own "is there a new service worker?" check — which only
 * runs on a real page load — almost never happened; and when it did, the new
 * worker installed quietly while the old page carried on, so the new version
 * only appeared on the launch AFTER that. Kill, relaunch, kill, relaunch.
 *
 * What this does instead:
 *   1. ASKS — every time the app comes to the foreground (at most once a
 *      minute), and every 30 minutes while it stays open.
 *   2. SWITCHES — when a new version has downloaded, it takes over and the
 *      page reloads once, by itself, provided that is safe…
 *   3. …and "safe" means nobody loses typing: not while a field has focus,
 *      the page editor is open, or a new/edit form or the Phone Log is on
 *      screen. It also only yanks the page in the first seconds after you
 *      open the app (or while it is in the background) — never in the middle
 *      of reading. Otherwise a small "New version ready" pill offers the
 *      reload, and the switch happens on its own next time you open the app.
 */
import { registerSW } from 'virtual:pwa-register'

const CHECK_EVERY_MS = 60 * 1000 // foreground checks, at most this often
const BACKGROUND_EVERY_MS = 30 * 60 * 1000
const FRESH_MS = 15 * 1000 // "just opened" — a reload now interrupts nothing

let updateSW = null
let registration = null
let pending = false
let visibleSince = Date.now()
let lastCheck = 0
const listeners = new Set()

export const subscribe = (fn) => (listeners.add(fn), () => listeners.delete(fn))
export const isUpdatePending = () => pending
const notify = () => listeners.forEach((fn) => fn())

/** Would a reload right now throw away something the user is doing? */
function busy() {
  const el = document.activeElement
  if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return true
  if (document.querySelector('.bn-editor')) return true // the page editor is open
  const path = window.location.pathname
  return /\/(new|edit)$/.test(path) || path.startsWith('/phone')
}

/** Reload into the new version. Safe to call from the pill at any time. */
export function applyUpdate() {
  if (!updateSW) return window.location.reload()
  updateSW(true) // tells the waiting worker to take over; reloads when it has
  // Belt and braces: if the hand-over event never arrives, reload anyway.
  setTimeout(() => window.location.reload(), 4000)
}

function tryApply() {
  if (!pending) return
  const fresh = document.visibilityState === 'hidden' || Date.now() - visibleSince < FRESH_MS
  if (fresh && !busy()) applyUpdate()
  else notify() // the pill stays up; the next foreground gets another go
}

function check() {
  if (!registration || Date.now() - lastCheck < CHECK_EVERY_MS) return
  if (!navigator.onLine) return
  lastCheck = Date.now()
  registration.update().catch(() => {}) // offline or mid-deploy: try again later
}

export function startAppUpdates() {
  if (!('serviceWorker' in navigator)) return
  updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_url, reg) {
      registration = reg || null
      setInterval(check, BACKGROUND_EVERY_MS)
    },
    onNeedRefresh() {
      pending = true
      notify()
      tryApply()
    },
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return tryApply() // backgrounded: a free moment
    visibleSince = Date.now()
    check()
    tryApply()
  })
  // Safari fires pageshow (not visibilitychange) when it restores the app
  // from its back-forward cache.
  window.addEventListener('pageshow', () => {
    visibleSince = Date.now()
    check()
    tryApply()
  })
}
